'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@/lib/use-debounce';
import { BundleProvider, useBundle } from './bundle-context';
import { BundleGrid } from './bundle-grid';
import { BundlePanel } from './bundle-panel';
import { BundleSearchControls } from './bundle-search-controls';
import { BundleStickyBar } from './bundle-sticky-bar';
import type { BundleInitialData, BundleProduct, BundleSortValue } from './bundle-types';
import { useBundleCheckout } from './use-bundle-checkout';
export interface BundleBuilderClientProps {
  eyebrow?: string;
  heading: string;
  subheading?: string;
  initialData: BundleInitialData;
}
export function BundleBuilderContent(t0) {
  const $ = _c(10);
  const {
    eyebrow,
    heading,
    subheading,
    initialData
  } = t0;
  const fallbackCurrency = initialData.catalog[0]?.priceRange.minVariantPrice.currencyCode ?? "USD";
  let t1;
  if ($[0] !== eyebrow || $[1] !== heading || $[2] !== initialData || $[3] !== subheading) {
    t1 = <BundleExperience eyebrow={eyebrow} heading={heading} subheading={subheading} initialData={initialData} />;
    $[0] = eyebrow;
    $[1] = heading;
    $[2] = initialData;
    $[3] = subheading;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== fallbackCurrency || $[6] !== initialData.moneyLocale || $[7] !== initialData.settings || $[8] !== t1) {
    t2 = <BundleProvider settings={initialData.settings} moneyLocale={initialData.moneyLocale} fallbackCurrencyCode={fallbackCurrency}>{t1}</BundleProvider>;
    $[5] = fallbackCurrency;
    $[6] = initialData.moneyLocale;
    $[7] = initialData.settings;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
function buildHaystack(product: BundleProduct, tradeNameBySlug: Map<string, string>): string {
  const tradeNames = product.tradeHandles.map(slug => tradeNameBySlug.get(slug) ?? slug);
  return [product.title, product.handle, product.productType, ...(product.tags ?? []), ...product.tradeHandles, ...tradeNames].join(' ').toLowerCase();
}
function priceOf(product: BundleProduct): number {
  return Number.parseFloat(product.priceRange.minVariantPrice.amount) || 0;
}
function sortProducts(products: BundleProduct[], sortValue: BundleSortValue): BundleProduct[] {
  switch (sortValue) {
    case 'price-asc':
      return [...products].sort((a, b) => priceOf(a) - priceOf(b));
    case 'price-desc':
      return [...products].sort((a, b) => priceOf(b) - priceOf(a));
    case 'title':
      return [...products].sort((a, b) => a.title.localeCompare(b.title));
    default:
      // best-selling / newest → preserve the server fetch order.
      return products;
  }
}
function BundleExperience(t0) {
  const $ = _c(77);
  const {
    eyebrow,
    heading,
    subheading,
    initialData
  } = t0;
  const {
    settings,
    entries,
    clear
  } = useBundle();
  const {
    addBundle,
    isSubmitting
  } = useBundleCheckout(settings);
  const [query, setQuery] = useState("");
  const [activeTrade, setActiveTrade] = useState(null);
  const [sortValue, setSortValue] = useState(settings.grid.sortOptions[0]?.value ?? "best-selling");
  const debouncedQuery = useDebounce(query, 200);
  const searchInputRef = useRef(null);
  let t1;
  if ($[0] !== initialData.trades) {
    t1 = new Map(initialData.trades.map(_temp));
    $[0] = initialData.trades;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const tradeNameBySlug = t1;
  let t2;
  if ($[2] !== initialData.catalog) {
    t2 = new Map(initialData.catalog.map(_temp2));
    $[2] = initialData.catalog;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const productById = t2;
  let t3;
  bb0: {
    if (initialData.popularHandles.length === 0) {
      t3 = initialData.catalog;
      break bb0;
    }
    let t4;
    if ($[4] !== initialData.popularHandles || $[5] !== productById) {
      let t5;
      if ($[7] !== productById) {
        t5 = handle => productById.get(handle);
        $[7] = productById;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      t4 = initialData.popularHandles.map(t5).filter(_temp3);
      $[4] = initialData.popularHandles;
      $[5] = productById;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    const ordered = t4;
    t3 = ordered.length > 0 ? ordered : initialData.catalog;
  }
  const popularProducts = t3;
  let t4;
  if ($[9] !== initialData.catalog || $[10] !== tradeNameBySlug) {
    let t5;
    if ($[12] !== tradeNameBySlug) {
      t5 = product_1 => ({
        product: product_1,
        haystack: buildHaystack(product_1, tradeNameBySlug)
      });
      $[12] = tradeNameBySlug;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = initialData.catalog.map(t5);
    $[9] = initialData.catalog;
    $[10] = tradeNameBySlug;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const searchIndex = t4;
  let t5;
  if ($[14] !== activeTrade || $[15] !== debouncedQuery || $[16] !== popularProducts || $[17] !== searchIndex) {
    const normalizedQuery = debouncedQuery.trim().toLowerCase();
    const isSearching = normalizedQuery.length > 0 || activeTrade !== null;
    t5 = isSearching ? searchIndex.filter(t6 => {
      const {
        haystack
      } = t6;
      return !normalizedQuery || haystack.includes(normalizedQuery);
    }).map(_temp4) : popularProducts;
    $[14] = activeTrade;
    $[15] = debouncedQuery;
    $[16] = popularProducts;
    $[17] = searchIndex;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let results = t5;
  if (activeTrade) {
    let t6;
    if ($[19] !== activeTrade || $[20] !== results) {
      let t7;
      if ($[22] !== activeTrade) {
        t7 = product_3 => product_3.tradeHandles.includes(activeTrade);
        $[22] = activeTrade;
        $[23] = t7;
      } else {
        t7 = $[23];
      }
      t6 = results.filter(t7);
      $[19] = activeTrade;
      $[20] = results;
      $[21] = t6;
    } else {
      t6 = $[21];
    }
    results = t6;
  }
  let t6;
  if ($[24] !== results || $[25] !== sortValue) {
    t6 = sortProducts(results, sortValue);
    $[24] = results;
    $[25] = sortValue;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const filtered = t6;
  let t7;
  let t8;
  if ($[27] !== initialData.showKeyboardHint) {
    t7 = () => {
      if (!initialData.showKeyboardHint) {
        return;
      }
      const handler = event => {
        if (event.key !== "/" || event.defaultPrevented) {
          return;
        }
        const target = event.target as HTMLElement | null;
        const tag = target?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || target?.isContentEditable) {
          return;
        }
        event.preventDefault();
        searchInputRef.current?.focus();
      };
      document.addEventListener("keydown", handler);
      return () => document.removeEventListener("keydown", handler);
    };
    t8 = [initialData.showKeyboardHint];
    $[27] = initialData.showKeyboardHint;
    $[28] = t7;
    $[29] = t8;
  } else {
    t7 = $[28];
    t8 = $[29];
  }
  useEffect(t7, t8);
  let t9;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setQuery("");
      setActiveTrade(null);
    };
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const clearFilters = t9;
  let t10;
  if ($[31] !== addBundle || $[32] !== clear || $[33] !== entries) {
    t10 = () => {
      addBundle(entries).then(cart => {
        if (cart) {
          clear();
        }
      });
    };
    $[31] = addBundle;
    $[32] = clear;
    $[33] = entries;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const handleDesktopAdd = t10;
  let t11;
  if ($[35] !== addBundle || $[36] !== entries) {
    t11 = () => {
      addBundle(entries, {
        redirectToCheckout: true
      });
    };
    $[35] = addBundle;
    $[36] = entries;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  const handleMobileCheckout = t11;
  let t12;
  if ($[38] !== eyebrow) {
    t12 = eyebrow && <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-accent">{eyebrow}</p>;
    $[38] = eyebrow;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== heading) {
    t13 = <h1 className="mt-1 text-3xl font-black uppercase leading-[0.95] tracking-tight text-neutral-950 sm:text-4xl">{heading}</h1>;
    $[40] = heading;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== subheading) {
    t14 = subheading && <p className="mt-3 max-w-xl text-neutral-600">{subheading}</p>;
    $[42] = subheading;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  let t15;
  if ($[44] !== t12 || $[45] !== t13 || $[46] !== t14) {
    t15 = <header className="mb-6">{t12}{t13}{t14}</header>;
    $[44] = t12;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = value => setSortValue(value as BundleSortValue);
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== activeTrade || $[50] !== initialData.keyboardHintText || $[51] !== initialData.showKeyboardHint || $[52] !== initialData.trades || $[53] !== query || $[54] !== settings.grid.sortOptions || $[55] !== settings.tradeSource.showTradePills || $[56] !== sortValue) {
    t17 = <BundleSearchControls query={query} onQueryChange={setQuery} searchInputRef={searchInputRef} trades={initialData.trades} activeTrade={activeTrade} onTradeChange={setActiveTrade} sortValue={sortValue} onSortChange={t16} sortOptions={settings.grid.sortOptions} showTradePills={settings.tradeSource.showTradePills} showKeyboardHint={initialData.showKeyboardHint} keyboardHintText={initialData.keyboardHintText} />;
    $[49] = activeTrade;
    $[50] = initialData.keyboardHintText;
    $[51] = initialData.showKeyboardHint;
    $[52] = initialData.trades;
    $[53] = query;
    $[54] = settings.grid.sortOptions;
    $[55] = settings.tradeSource.showTradePills;
    $[56] = sortValue;
    $[57] = t17;
  } else {
    t17 = $[57];
  }
  let t18;
  if ($[58] !== filtered || $[59] !== initialData.columns) {
    t18 = <div className="mt-6"><BundleGrid products={filtered} columns={initialData.columns} onClearFilters={clearFilters} /></div>;
    $[58] = filtered;
    $[59] = initialData.columns;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== t15 || $[62] !== t17 || $[63] !== t18) {
    t19 = <div className="lg:col-span-8">{t15}{t17}{t18}</div>;
    $[61] = t15;
    $[62] = t17;
    $[63] = t18;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  let t20;
  if ($[65] !== handleDesktopAdd || $[66] !== isSubmitting) {
    t20 = <aside className="hidden lg:col-span-4 lg:block"><BundlePanel className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)]" onAddToCart={handleDesktopAdd} isSubmitting={isSubmitting} /></aside>;
    $[65] = handleDesktopAdd;
    $[66] = isSubmitting;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  let t21;
  if ($[68] !== t19 || $[69] !== t20) {
    t21 = <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10">{t19}{t20}</div>;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  let t22;
  if ($[71] !== handleMobileCheckout || $[72] !== isSubmitting) {
    t22 = <BundleStickyBar onCheckout={handleMobileCheckout} isSubmitting={isSubmitting} />;
    $[71] = handleMobileCheckout;
    $[72] = isSubmitting;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  let t23;
  if ($[74] !== t21 || $[75] !== t22) {
    t23 = <div className="container-page pb-44 lg:pb-0">{t21}{t22}</div>;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  return t23;
}
function _temp4(t0) {
  const {
    product: product_2
  } = t0;
  return product_2;
}
function _temp3(product_0) {
  return Boolean(product_0);
}
function _temp2(product) {
  return [product.handle, product];
}
function _temp(trade) {
  return [trade.slug, trade.name];
}